// @ts-strict-ignore
import {
  CONTENTFUL_BLOG_POST_QUERY,
  CONTENTFUL_CONTENT_TYPES,
  CONTENTFUL_DEFAULT_SORT,
  CONTENTFUL_REQUEST_LIMIT_FIRST_PAGE,
  CONTENTFUL_REQUEST_LIMIT_PER_PAGE
} from "constants/api-constants";
import type { Entry, EntryCollection } from "contentful";
import type {
  BlogAuthorContentModel,
  CategoriesListContentModel,
  BlogPostContentModel
} from "types/contentful";
import { contentfulClient as client } from "../contentful";
import { validateLocale } from "utils/validateLocale";

// default blogPost queries to en-US since they are not translated
export const BlogAPI = {
  fetchBlogCategories: (
    locale?: string
  ): Promise<Entry<CategoriesListContentModel>> => {
    return client.getEntry("16ZjgQgWT2WQzJWeycmj8f", {
      locale: validateLocale(locale)
    });
  },
  fetchBlogPosts: (
    page?: number, // 0 indexed page number
    idsToExclude?: string[]
  ): Promise<EntryCollection<BlogPostContentModel>> => {
    const query = {
      ...CONTENTFUL_BLOG_POST_QUERY,
      locale: "en-US",
      limit:
        page === 0
          ? CONTENTFUL_REQUEST_LIMIT_FIRST_PAGE
          : CONTENTFUL_REQUEST_LIMIT_PER_PAGE,
      skip:
        page !== 0
          ? CONTENTFUL_REQUEST_LIMIT_FIRST_PAGE +
            (page - 1) * CONTENTFUL_REQUEST_LIMIT_PER_PAGE
          : 0,
      "fields.datePublished[exists]": true // added to ignore entries that are not published
    };

    if (idsToExclude && idsToExclude.length > 0) {
      query[`sys.id[nin]`] = idsToExclude.join(",");
    }

    return client.getEntries(query);
  },

  // Used to pre-render blog/{slug} paths; only 5 blog post articles should be pre-rendered
  fetchHomepageBlogPosts: (): Promise<EntryCollection<BlogPostContentModel>> =>
    client.getEntries({
      ...CONTENTFUL_BLOG_POST_QUERY,
      locale: "en-US",
      // Fetch only 5 entries
      limit: 5
    }),

  fetchLargeFeaturedPost: (): Promise<EntryCollection<BlogPostContentModel>> =>
    client.getEntries({
      ...CONTENTFUL_BLOG_POST_QUERY,
      locale: "en-US",
      "fields.isLargeFeatured": true,
      limit: 1
    }),

  fetchSmallFeaturedPost: (
    limit: number,
    categoryId?: string
  ): Promise<EntryCollection<BlogPostContentModel>> => {
    return client.getEntries({
      ...CONTENTFUL_BLOG_POST_QUERY,
      locale: "en-US",
      "fields.isSmallFeatured": true,
      limit: limit,
      //On the categories page we show all small articles, even if they are large featured at the same time
      ...(!categoryId && { "fields.isLargeFeatured": false }),
      ...(categoryId && { "fields.blogType.sys.type": categoryId })
    });
  },

  fetchFeaturedContributors: (
    locale?: string
  ): Promise<EntryCollection<BlogAuthorContentModel>> =>
    client.getEntries({
      content_type: CONTENTFUL_CONTENT_TYPES.BLOG_AUTHOR,
      locale: validateLocale(locale),
      "fields.featuredContributor": true,
      order: "sys.createdAt",
      limit: 100
    }),

  fetchBlogPostsWithFilter: (
    filterID: string,
    skip?: number,
    limit?: number
  ): Promise<EntryCollection<BlogPostContentModel>> =>
    client.getEntries({
      content_type: CONTENTFUL_CONTENT_TYPES.BLOG_POST,
      order: CONTENTFUL_DEFAULT_SORT,
      locale: "en-US",
      ...(skip && { skip }),
      limit: limit || 1000,
      "fields.blogType.sys.type": filterID
    }),

  fetchSingleBlogPost: (
    slug: string
  ): Promise<EntryCollection<BlogPostContentModel>> =>
    client.getEntries({
      content_type: CONTENTFUL_CONTENT_TYPES.BLOG_POST,
      locale: "en-US",
      "fields.slug": slug
    }),

  fetchAllAuthors: (
    locale?: string
  ): Promise<EntryCollection<BlogAuthorContentModel>> =>
    client.getEntries({
      content_type: CONTENTFUL_CONTENT_TYPES.BLOG_AUTHOR,
      locale: validateLocale(locale),
      limit: 1000
    }),

  fetchAuthor: (
    slug: string,
    locale?: string
  ): Promise<EntryCollection<BlogAuthorContentModel>> =>
    client.getEntries({
      content_type: CONTENTFUL_CONTENT_TYPES.BLOG_AUTHOR,
      limit: 1,
      locale: validateLocale(locale),
      "fields.slug": slug
    }),

  fetchBlogPostsByAuthor: (
    authorId: string,
    skip?: number
  ): Promise<EntryCollection<BlogPostContentModel>> =>
    client.getEntries({
      content_type: CONTENTFUL_CONTENT_TYPES.BLOG_POST,
      order: CONTENTFUL_DEFAULT_SORT,
      locale: "en-US",
      links_to_entry: authorId,
      ...(skip && { skip }),
      limit: 1000
    }),

  fetchSimilarPosts: (
    categoryTypeIds: Array<string>,
    slug: string
  ): Promise<EntryCollection<BlogPostContentModel>> =>
    client.getEntries({
      content_type: CONTENTFUL_CONTENT_TYPES.BLOG_POST,
      order: CONTENTFUL_DEFAULT_SORT,
      locale: "en-US",
      links_to_entry: categoryTypeIds,
      limit: 3,
      "fields.slug[ne]": slug
    }),

  fetchMorePostsFromAuthor: (
    authorId: string,
    slug: string
  ): Promise<EntryCollection<BlogPostContentModel>> =>
    client.getEntries({
      content_type: CONTENTFUL_CONTENT_TYPES.BLOG_POST,
      order: CONTENTFUL_DEFAULT_SORT,
      locale: "en-US",
      limit: 3,
      links_to_entry: authorId,
      "fields.slug[ne]": slug
    }),
  fetchCoinRelatedPosts: (
    coinEntryIds: Array<string>,
    limit?: number
  ): Promise<EntryCollection<BlogPostContentModel>> =>
    client.getEntries({
      content_type: CONTENTFUL_CONTENT_TYPES.BLOG_POST,
      order: CONTENTFUL_DEFAULT_SORT,
      locale: "en-US",
      "fields.relatedCoins.sys.id[in]": coinEntryIds.join(","),
      limit: limit ?? 3
    })
};
